/**
 * Utility para carregar dinamicamente o script do Gigya com a chave de API apropriada
 */

let currentApiKey = null
let loadingPromise = null

/**
 * Carrega o script do Gigya dinamicamente
 * @param {string} apiKey - Chave de API do Gigya
 * @returns {Promise} - Promise que resolve quando o script é carregado e o Gigya está pronto
 */
export const loadGigyaScript = (apiKey) => {
  // Se já está carregando com a mesma chave, retorna a promise em andamento
  if (currentApiKey === apiKey && loadingPromise) {
    return loadingPromise
  }

  // Se já existe um script carregado mas com chave diferente, remove tudo
  if (currentApiKey && currentApiKey !== apiKey) {
    removeGigyaScript()
  }

  // Cria nova promise de carregamento
  loadingPromise = new Promise((resolve, reject) => {
    // Verifica se o Gigya já existe (caso o script já tenha sido carregado)
    if (window.gigya && window.gigya.accounts && currentApiKey === apiKey) {
      resolve()
      return
    }

    // Cria o novo script
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.lang = 'javascript'
    script.src = `https://cdns.gigya.com/js/gigya.js?apikey=${apiKey}`
    script.id = 'gigya-script'

    script.onload = () => {
      currentApiKey = apiKey

      // Espera o Gigya estar completamente carregado
      const checkGigya = setInterval(() => {
        if (window.gigya && window.gigya.accounts) {
          clearInterval(checkGigya)
          resolve()
        }
      }, 50)

      // Timeout de segurança (10 segundos)
      setTimeout(() => {
        clearInterval(checkGigya)
        if (window.gigya && window.gigya.accounts) {
          resolve()
        } else {
          reject(new Error('Timeout ao carregar Gigya'))
        }
      }, 10000)
    }

    script.onerror = () => {
      currentApiKey = null
      loadingPromise = null
      reject(new Error('Erro ao carregar script do Gigya'))
    }

    document.head.appendChild(script)
  })

  return loadingPromise
}

/**
 * Remove o script do Gigya completamente
 */
export const removeGigyaScript = () => {
  const script = document.getElementById('gigya-script')
  if (script) {
    script.remove()
  }

  // Tenta limpar o objeto gigya global
  // Nota: O Gigya pode tornar o objeto não-deletável, então usamos try-catch
  try {
    if (window.gigya) {
      delete window.gigya
    }
  } catch (e) {
    // Se não conseguir deletar, apenas define como undefined
    window.gigya = undefined
  }

  currentApiKey = null
  loadingPromise = null
}

/**
 * Verifica se o Gigya está carregado
 * @returns {boolean}
 */
export const isGigyaLoaded = () => {
  return !!(window.gigya && window.gigya.accounts && currentApiKey)
}

/**
 * Obtém a chave de API atualmente carregada
 * @returns {string|null}
 */
export const getCurrentApiKey = () => {
  return currentApiKey
}
